import { useRef, useEffect, useCallback } from "react";
import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { Search, X, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useGlobalSearch } from "@/hooks/use-global-search";
import { RecentSearches } from "./RecentSearches";
import { FilterTabs } from "./FilterTabs";
import { SearchResultGroup } from "./SearchResultGroup";
import { SearchResultItem } from "./SearchResultItem";

interface GlobalSearchMobileProps {
  open: boolean;
  onClose: () => void;
}

export function GlobalSearchMobile({ open, onClose }: GlobalSearchMobileProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const search = useGlobalSearch();
  const [location] = useLocation();

  // Close overlay on route change (e.g., browser back button)
  useEffect(() => {
    if (open) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // Auto-focus input when overlay opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    } else {
      search.clearQuery();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleItemClick = useCallback(() => {
    if (search.query.trim()) {
      search.saveRecentSearch(search.query);
    }
    search.clearQuery();
    onClose();
  }, [search, onClose]);

  const handleShowAll = useCallback(
    (type: string) => {
      search.setActiveFilter(type);
    },
    [search],
  );

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[110] bg-background flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={t("globalSearch.mobileOverlayLabel", "Search")}
      data-testid="global-search-mobile-overlay"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b shrink-0">
        <button
          onClick={onClose}
          className="p-2 -ml-1 rounded-md hover:bg-accent transition-colors"
          aria-label={t("globalSearch.close", "Close search")}
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            ref={inputRef}
            type="text"
            value={search.query}
            onChange={(e) => search.setQuery(e.target.value)}
            placeholder={t("globalSearch.mobilePlaceholder", "Search festivals, journeys...")}
            className="pl-9 pr-8 h-10"
            aria-label={t("globalSearch.mobilePlaceholder", "Search festivals, journeys...")}
            data-testid="global-search-mobile-input"
          />
          {search.query && (
            <button
              onClick={() => search.clearQuery()}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={t("globalSearch.clearInput", "Clear search")}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Filter tabs */}
      {search.isSearching && (
        <FilterTabs active={search.activeFilter} onChange={search.setActiveFilter} />
      )}

      {/* Results */}
      <div className="flex-1 overflow-y-auto overscroll-contain">
        {!search.isSearching ? (
          <>
            <RecentSearches
              searches={search.recentSearches}
              onSelect={(term) => search.setQuery(term)}
              onClear={search.clearRecentSearches}
            />
            {search.recentSearches.length === 0 && (
              <div className="px-4 py-12 text-center">
                <Search className="h-10 w-10 mx-auto mb-3 text-muted-foreground/60" />
                <p className="text-sm text-muted-foreground">
                  {t("globalSearch.startTyping", "Start typing to search festivals and journeys")}
                </p>
              </div>
            )}
          </>
        ) : search.activeFilter === "all" ? (
          // Grouped view
          search.groupedResults.length === 0 ? (
            <div className="px-4 py-12 text-center">
              <Search className="h-10 w-10 mx-auto mb-3 text-muted-foreground/60" />
              <p className="text-sm text-muted-foreground">
                {t("globalSearch.noResults", "No results found")}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t("globalSearch.tryDifferent", "Try a different search term")}
              </p>
            </div>
          ) : (
            search.groupedResults.map((group) => (
              <SearchResultGroup
                key={group.type}
                group={group}
                onItemClick={handleItemClick}
                onShowAll={handleShowAll}
              />
            ))
          )
        ) : // Filtered view (flat list)
        search.filteredResults.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <p className="text-sm text-muted-foreground">
              {t("globalSearch.noResults", "No results found")}
            </p>
          </div>
        ) : (
          search.filteredResults.map((item) => (
            <SearchResultItem
              key={`${item.type}-${item.id}`}
              entry={item}
              onClick={handleItemClick}
            />
          ))
        )}
      </div>
    </div>
  );
}
